import React, { useMemo, memo } from 'react';
import { ChevronDown } from 'lucide-react';
import { useModelStore, findModelById } from '@/stores/modelStore';

interface ModelSelectorProps {
  selectedModel: string;
  mode: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
}

const ModelSelectorInner: React.FC<ModelSelectorProps> = ({
  selectedModel,
  mode,
  onChange,
  disabled = false,
}) => {
  const models = useModelStore((s) => s.models);
  const providerVisibility = useModelStore((s) => s.providerVisibility);
  // 变体反查：存量节点引用同名聚合的变体 id（如 -vip）时仍能显示聚合模型名
  const selectedModelInfo = useMemo(() => findModelById(models, selectedModel), [models, selectedModel]);

  const filteredModels = useMemo(() => models.filter((m) => {
    if (!m.supportedModes.includes(mode)) return false;
    const visible = providerVisibility[m.backend];
    return visible !== false;
  }), [models, mode, providerVisibility]);

  return (
    <div className="relative group/model">
      <div
        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-white/5 cursor-default transition-colors text-[10px] font-bold text-slate-400 hover:text-cyan-400 ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <span>{selectedModelInfo?.name || 'AI Model'}</span>
        <ChevronDown size={10} />
      </div>
      <div
        className="absolute bottom-full left-0 pb-2 w-40 opacity-0 translate-y-2 pointer-events-none group-hover/model:opacity-100 group-hover/model:translate-y-0 group-hover/model:pointer-events-auto transition-all duration-200 z-[200]"
      >
        <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden">
          {filteredModels.map((m) => (
            <div
              key={m.id}
              onClick={() => !disabled && onChange(m.id)}
              className={`px-3 py-2 text-[10px] font-bold cursor-pointer hover:bg-white/10 ${
                selectedModel === m.id ? 'text-cyan-400 bg-white/5' : 'text-slate-400'
              } ${disabled ? 'cursor-not-allowed' : ''}`}
            >
              {m.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ModelSelector = memo(ModelSelectorInner, (prevProps, nextProps) => {
  return (
    prevProps.selectedModel === nextProps.selectedModel &&
    prevProps.mode === nextProps.mode &&
    prevProps.disabled === nextProps.disabled
  );
});